import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import usePageTitle from '../../hooks/usePageTitle';
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandshake, faTrash, faArrowLeft, faMagnifyingGlass, faXmark,
  faPlus, faRotateLeft, faUpload,
} from "@fortawesome/free-solid-svg-icons";
import Sidebar from "../../components/Dashboard/Sidebar";
import {
  listSupporters,
  createSupporter,
  updateSupporter,
  deleteSupporter,
} from "../../api/supporters";
import { uploadSingleFile } from "../../api/epks";

const emptyDraft = { name: "", websiteUrl: "", logoKey: "", active: true };

export default function SupportersPage() {
  const { t } = useTranslation();
  usePageTitle(t('Admin Supporters'));
  const user = useSelector((state) => state.user);

  const [supporters, setSupporters] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [mobileView, setMobileView] = useState("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const IMAGE_URL_PREFIX = `${process.env.REACT_APP_AWS_URL}`;

  const load = useCallback(async () => {
    try {
      const data = await listSupporters(true);
      setSupporters(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(t("Could not load supporters."));
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const filtered = supporters.filter((s) =>
    (s.name || "").toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const startNew = () => {
    setSelected(null);
    setDraft(emptyDraft);
    setConfirmDeleteId(null);
    setMobileView("detail");
  };

  const startEdit = (supporter) => {
    setSelected(supporter);
    setDraft({
      name: supporter.name || "",
      websiteUrl: supporter.websiteUrl || "",
      logoKey: supporter.logoKey || "",
      active: supporter.active !== false,
    });
    setConfirmDeleteId(null);
    setMobileView("detail");
  };

  const handleLogoChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const key = await uploadSingleFile(file);
      setDraft((prev) => ({ ...prev, logoKey: key }));
    } catch (error) {
      toast.error(t("Logo upload failed."));
    } finally {
      setIsUploading(false);
      event.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error(t("Supporter name is required"));
      return;
    }
    setIsSaving(true);
    try {
      if (selected) {
        await updateSupporter(selected._id, draft);
        toast.success(t("Supporter updated."));
      } else {
        await createSupporter(draft);
        toast.success(t("Supporter created."));
      }
      await load();
      setSelected(null);
      setDraft(emptyDraft);
      setMobileView("list");
    } catch (error) {
      const status = error?.response?.status;
      if (status === 409) toast.error(t("A supporter with that name already exists"));
      else if (status === 403) toast.error(t("Only admins can manage supporters."));
      else toast.error(t("Could not save the supporter."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (supporter) => {
    try {
      await deleteSupporter(supporter._id);
      toast.success(t("Supporter deactivated."));
      await load();
      if (selected?._id === supporter._id) {
        setSelected(null);
        setDraft(emptyDraft);
      }
    } catch (error) {
      toast.error(t("Could not deactivate the supporter."));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleReactivate = async (supporter) => {
    try {
      await updateSupporter(supporter._id, { active: true });
      toast.success(t("Supporter reactivated."));
      await load();
    } catch (error) {
      toast.error(t("Could not reactivate the supporter."));
    }
  };

  const logoPreview = draft.logoKey
    ? `${IMAGE_URL_PREFIX}/${draft.logoKey}`
    : null;

  return (
    <div className="tw-flex tw-h-[calc(100vh-5rem)] tw-overflow-hidden tw-bg-[#1E0039]">
      <div className="tw-flex tw-flex-1 tw-overflow-hidden tw-pt-3 tw-pb-4 tw-px-3">
        <Sidebar role={user.role} />

        <div className="tw-flex-1 tw-flex tw-overflow-hidden tw-gap-3 md:tw-ml-3">
          <div className={`tw-flex-col tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-overflow-hidden
            tw-w-full md:tw-w-72 md:tw-shrink-0 md:tw-flex
            ${mobileView === "list" ? "tw-flex" : "tw-hidden"}`}>

            <div className="tw-px-3 tw-pt-3 tw-pb-2 tw-shrink-0">
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <FontAwesomeIcon icon={faHandshake} className="tw-text-[#712CB0]" />
                  <h2 className="tw-text-sm tw-font-bold tw-text-white tw-mb-0">{t("Supporters")}</h2>
                </div>
                <span className="tw-text-xs tw-text-white/40">
                  {filtered.length} {t("total")}
                </span>
              </div>

              <div className="tw-relative tw-mb-2">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-white/40 tw-text-xs"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("Search supporters")}
                  className="tw-w-full tw-bg-[#1E0039] tw-text-white tw-text-xs tw-rounded-lg tw-border tw-border-white/10 tw-py-2 tw-pl-8 tw-pr-3 focus:tw-outline-none focus:tw-border-[#FF43A7]"
                />
              </div>

              <button
                onClick={startNew}
                className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2 tw-rounded-lg tw-bg-[#FF43A7] hover:tw-bg-[#e63c96] tw-border-none tw-text-white tw-text-xs tw-font-bold tw-cursor-pointer"
              >
                <FontAwesomeIcon icon={faPlus} />
                {t("New Supporter")}
              </button>
            </div>

            <div className="tw-flex-1 tw-overflow-y-auto tw-px-3 tw-pb-3">
              {filtered.length === 0 && (
                <p className="tw-text-white/40 tw-text-xs tw-text-center tw-py-6 tw-mb-0">
                  {t("No supporters yet.")}
                </p>
              )}

              {filtered.map((supporter) => (
                <button
                  key={supporter._id}
                  onClick={() => startEdit(supporter)}
                  className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-text-left tw-rounded-lg tw-p-2 tw-mb-1.5 tw-border tw-cursor-pointer tw-transition-colors ${
                    selected?._id === supporter._id
                      ? "tw-bg-[#1E0039] tw-border-[#FF43A7]"
                      : "tw-bg-[#1E0039] tw-border-transparent hover:tw-border-white/20"
                  }`}
                >
                  <div className="tw-w-8 tw-h-8 tw-shrink-0 tw-rounded tw-bg-white tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
                    {supporter.logoKey ? (
                      <img
                        src={`${IMAGE_URL_PREFIX}/${supporter.logoKey}`}
                        alt={supporter.name}
                        className="tw-max-w-full tw-max-h-full tw-object-contain"
                      />
                    ) : (
                      <span className="tw-text-[#1F0439] tw-text-[10px] tw-font-bold">
                        {(supporter.name || "?").slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="tw-flex-1 tw-text-white tw-text-xs tw-font-bold tw-truncate">
                    {supporter.name}
                  </span>
                  {supporter.active === false && (
                     <span className="tw-text-[9px] tw-uppercase tw-tracking-wider tw-text-white/40">
                      {t("Inactive")}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className={`tw-flex-1 tw-flex-col tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-overflow-hidden
            md:tw-flex ${mobileView === "detail" ? "tw-flex" : "tw-hidden"}`}>

            <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-border-b tw-border-white/10 tw-shrink-0">
              <button
                onClick={() => setMobileView("list")}
                aria-label={t("Back")}
                className="md:tw-hidden tw-bg-transparent tw-border-none tw-text-white tw-cursor-pointer tw-p-1"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <h2 className="tw-text-sm tw-font-bold tw-text-white tw-mb-0">
                {selected ? t("Edit Supporter") : t("New Supporter")}
              </h2>
            </div>

            <div className="tw-flex-1 tw-overflow-y-auto tw-p-4 md:tw-p-6">
              <div className="tw-max-w-[520px] tw-flex tw-flex-col tw-gap-4">
                <label className="tw-flex tw-flex-col tw-gap-1.5">
                  <span className="tw-text-white/60 tw-text-[10px] tw-uppercase tw-tracking-widest tw-font-bold">
                    {t("Supporter Name")}
                  </span>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                    className="tw-bg-[#1E0039] tw-text-white tw-text-sm tw-rounded-lg tw-border tw-border-white/10 tw-py-2.5 tw-px-3 focus:tw-outline-none focus:tw-border-[#FF43A7]"
                  />
                </label>

                <label className="tw-flex tw-flex-col tw-gap-1.5">
                  <span className="tw-text-white/60 tw-text-[10px] tw-uppercase tw-tracking-widest tw-font-bold">
                    {t("Website URL")}
                  </span>
                  <input
                    type="url"
                    value={draft.websiteUrl}
                    onChange={(e) => setDraft((p) => ({ ...p, websiteUrl: e.target.value }))}
                    placeholder="https://"
                    className="tw-bg-[#1E0039] tw-text-white tw-text-sm tw-rounded-lg tw-border tw-border-white/10 tw-py-2.5 tw-px-3 focus:tw-outline-none focus:tw-border-[#FF43A7]"
                  />
                </label>

                <div className="tw-flex tw-flex-col tw-gap-1.5">
                  <span className="tw-text-white/60 tw-text-[10px] tw-uppercase tw-tracking-widest tw-font-bold">
                    {t("Logo")}
                  </span>
                  <div className="tw-flex tw-items-center tw-gap-4">
                    <div className="tw-w-24 tw-h-24 tw-shrink-0 tw-rounded-lg tw-bg-white tw-flex tw-items-center tw-justify-center tw-overflow-hidden tw-p-2">
                      {logoPreview ? (
                        <img
                          src={logoPreview}
                          alt={draft.name || t("Logo")}
                          className="tw-max-w-full tw-max-h-full tw-object-contain"
                        />
                      ) : (
                        <span className="tw-text-[#1F0439]/40 tw-text-[10px] tw-text-center">
                          {t("No logo")}
                        </span>
                      )}
                    </div>

                    <div className="tw-flex tw-flex-col tw-gap-2">
                      <label className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-[#1E0039] tw-border tw-border-white/10 tw-text-white tw-text-xs tw-cursor-pointer hover:tw-border-[#FF43A7]">
                        <FontAwesomeIcon icon={faUpload} />
                        {isUploading ? t("Uploading...") : t("Upload logo")}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoChange}
                          disabled={isUploading}
                          className="tw-hidden"
                        />
                      </label>

                      {draft.logoKey && (
                        <button
                          onClick={() => setDraft((p) => ({ ...p, logoKey: "" }))}
                          className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-transparent tw-border tw-border-white/10 tw-text-white/60 tw-text-xs tw-cursor-pointer hover:tw-text-white"
                        >
                          <FontAwesomeIcon icon={faXmark} />
                          {t("Remove logo")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-pt-2">
                  <button
                    onClick={handleSave}
                    disabled={isSaving || isUploading}
                    className="tw-px-5 tw-py-2.5 tw-rounded-lg tw-bg-[#FF43A7] hover:tw-bg-[#e63c96] tw-border-none tw-text-white tw-text-sm tw-font-bold tw-cursor-pointer disabled:tw-opacity-50"
                  >
                    {isSaving ? t("Saving...") : t("Save Supporter")}
                  </button>

                  {selected && selected.active !== false && (
                    confirmDeleteId === selected._id ? (
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-text-white/60 tw-text-xs">{t("Deactivate this supporter?")}</span>
                        <button
                          onClick={() => handleDelete(selected)}
                          className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-red-500 hover:tw-bg-red-600 tw-border-none tw-text-white tw-text-xs tw-font-bold tw-cursor-pointer"
                        >
                          {t("Confirm")}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="tw-px-3 tw-py-2 tw-rounded-lg tw-bg-transparent tw-border tw-border-white/20 tw-text-white tw-text-xs tw-cursor-pointer"
                        >
                          {t("Cancel")}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteId(selected._id)}
                        className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-lg tw-bg-transparent tw-border tw-border-red-500/50 tw-text-red-400 tw-text-sm tw-cursor-pointer hover:tw-bg-red-500/10"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        {t("Deactivate")}
                      </button>
                    )
                  )}

                  {selected && selected.active === false && (
                    <button
                      onClick={() => handleReactivate(selected)}
                      className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-rounded-lg tw-bg-transparent tw-border tw-border-white/20 tw-text-white tw-text-sm tw-cursor-pointer hover:tw-bg-white/5"
                    >
                      <FontAwesomeIcon icon={faRotateLeft} />
                      {t("Reactivate")}
                    </button>
                  )}
                </div>

                <p className="tw-text-white/40 tw-text-xs tw-mt-2 tw-mb-0">
                  {t("Deactivating a supporter hides it from EPK pages and from the supporter picker. Nothing is permanently deleted.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
